import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DollarSign, Plus, CheckCircle, Clock, XCircle, FileDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ServicePrices } from './ServicePrices';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const FinancialManagement = () => {
  const [payments, setPayments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: '', amount: '', description: '' });
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [payRes, patsRes] = await Promise.all([
        axios.get(`${API_URL}/api/payments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/patients`, { withCredentials: true })
      ]);
      setPayments(payRes.data);
      setPatients(patsRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/payments`, {
        ...form,
        amount: parseFloat(form.amount)
      }, { withCredentials: true });
      toast.success('Pagamento registrado!');
      setIsCreateOpen(false);
      setForm({ patient_id: '', amount: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar');
    }
  };

  const handleStatusChange = async (paymentId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/payments/${paymentId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Pagamento marcado como ${newStatus === 'paid' ? 'pago' : 'cancelado'}`);
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const getPatientName = (patientId) => {
    const patient = patients.find(p => p.patient_id === patientId);
    return patient?.name || patientId;
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/financial/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_financeiro_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório PDF gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar relatório PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const filteredPayments = statusFilter === 'all' ? payments : payments.filter(p => p.status === statusFilter);

  const totalPaid = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalPending = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalOverdue = payments.filter(p => p.status === 'overdue').reduce((sum, p) => sum + (p.amount || 0), 0);

  const statusColors = {
    paid: 'bg-[#0F1B3D]/10 text-[#0F1B3D]',
    pending: 'bg-[#E9C46A]/10 text-[#E9C46A]',
    overdue: 'bg-[#A8174E]/10 text-[#A8174E]',
    cancelled: 'bg-[#7A85A0]/10 text-[#7A85A0]'
  };

  const statusLabels = { paid: 'Pago', pending: 'Pendente', overdue: 'Atrasado', cancelled: 'Cancelado' };

  return (
    <div className="space-y-6" data-testid="financial-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Financeiro</h2>
          <p className="text-[#5F6B85]">Controle de pagamentos</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-[#E5DCC8] hover:bg-[#FAF6EE]"
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            data-testid="download-pdf-button"
          >
            {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
            {pdfLoading ? 'Gerando...' : 'Relatório PDF'}
          </Button>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="new-payment-button">
              <Plus className="h-4 w-4 mr-2" />
              Novo Pagamento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Registrar Pagamento</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Paciente *</Label>
                <Select value={form.patient_id} onValueChange={val => setForm({...form, patient_id: val})}>
                  <SelectTrigger data-testid="payment-patient-select">
                    <SelectValue placeholder="Selecione o paciente" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(p => (
                      <SelectItem key={p.patient_id} value={p.patient_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Valor (R$) *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required data-testid="payment-amount-input" />
              </div>
              <div className="space-y-2">
                <Label>Descrição *</Label>
                <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} required placeholder="Ex: Consulta de retorno" data-testid="payment-description-input" />
              </div>
              <Button type="submit" className="w-full bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="payment-submit-button">
                Registrar Pagamento
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[#E5DCC8]" data-testid="total-paid-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#0F1B3D]/10 rounded-lg">
                <CheckCircle className="h-5 w-5 text-[#0F1B3D]" />
              </div>
              <div>
                <p className="text-sm text-[#5F6B85]">Total Pago</p>
                <p className="text-2xl font-semibold text-[#0F1B3D]">R$ {totalPaid.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5DCC8]" data-testid="total-pending-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#E9C46A]/10 rounded-lg">
                <Clock className="h-5 w-5 text-[#E9C46A]" />
              </div>
              <div>
                <p className="text-sm text-[#5F6B85]">Pendente</p>
                <p className="text-2xl font-semibold text-[#E9C46A]">R$ {totalPending.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5DCC8]" data-testid="total-overdue-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#A8174E]/10 rounded-lg">
                <XCircle className="h-5 w-5 text-[#A8174E]" />
              </div>
              <div>
                <p className="text-sm text-[#5F6B85]">Atrasado</p>
                <p className="text-2xl font-semibold text-[#A8174E]">R$ {totalOverdue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="payment-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="paid">Pagos</SelectItem>
            <SelectItem value="pending">Pendentes</SelectItem>
            <SelectItem value="overdue">Atrasados</SelectItem>
            <SelectItem value="cancelled">Cancelados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        {filteredPayments.length === 0 ? (
          <Card className="border-[#E5DCC8]">
            <CardContent className="p-8 text-center">
              <p className="text-[#7A85A0]">Nenhum pagamento encontrado</p>
            </CardContent>
          </Card>
        ) : (
          filteredPayments.map((payment) => (
            <Card key={payment.payment_id} className="border-[#E5DCC8]" data-testid={`payment-card-${payment.payment_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-[#0F1B3D]">{getPatientName(payment.patient_id)}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[payment.status] || statusColors.pending}`}>
                        {statusLabels[payment.status] || payment.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-[#5F6B85]">
                      <span className="text-xl font-semibold text-[#0F1B3D]">R$ {(payment.amount || 0).toFixed(2)}</span>
                      <span>{payment.description}</span>
                      <span>{new Date(payment.created_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {payment.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(payment.payment_id, 'paid')}
                          className="bg-[#0F1B3D] hover:bg-[#0F1B3D]/80 text-white"
                          data-testid={`mark-paid-${payment.payment_id}`}
                        >
                          Marcar Pago
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(payment.payment_id, 'cancelled')}
                          className="text-[#A8174E]"
                          data-testid={`cancel-payment-${payment.payment_id}`}
                        >
                          Cancelar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Service Prices */}
      <ServicePrices />
    </div>
  );
};
